/**
 * RewriteBubble — floating toolbar that appears above a text selection inside
 * the document pane. Users can pick a preset (Shorter / More formal / Expand)
 * or type a custom instruction, and get a Claude-rewritten replacement.
 *
 * Selection tracking:
 *   - window selectionchange event (throttled via rAF) reads current selection
 *   - bubble only shows when selection is inside the target container (the paper)
 *     AND the selection is non-collapsed (has actual text)
 *   - position computed from selection's getBoundingClientRect
 *
 * Replacement:
 *   - On accept, caller is handed the new text + a token identifying the
 *     selected range (DOM Range snapshot) so it can replace the text in the
 *     source structured_content state.
 *   - A simple strategy: find-and-replace the selected text within the
 *     bullet/title/etc. that contains the anchor node. Works for most cases;
 *     ambiguity (same text appearing twice in the same element) is a known edge.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';

interface Props {
  meetingId: string;
  /** Ref to the container within which selections should trigger the bubble */
  containerRef: React.RefObject<HTMLElement | null>;
  /**
   * Called with { selectedText, rewrittenText } when the user confirms a rewrite.
   * The parent replaces the text in the source structured_content.
   */
  onApply: (replacement: { selectedText: string; rewrittenText: string }) => void;
}

interface Position {
  top: number;
  left: number;
}

const PRESETS: { label: string; instruction: string }[] = [
  { label: 'Shorter', instruction: 'Make it shorter - aim for roughly half the original length.' },
  { label: 'More formal', instruction: 'Rewrite in a more formal, professional tone.' },
  { label: 'Expand', instruction: 'Expand with more detail and context, while staying true to the facts.' },
  { label: 'Simpler', instruction: 'Simplify the language. Short sentences, everyday words.' },
];

export function RewriteBubble({ meetingId, containerRef, onApply }: Props) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const [selectedText, setSelectedText] = useState('');
  const [customMode, setCustomMode] = useState(false);
  const [customInstruction, setCustomInstruction] = useState('');
  const [rewritten, setRewritten] = useState<{ text: string; rationale?: string } | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  // Keep the last known selection so we can re-show the bubble after interacting
  // with its buttons (which collapses the selection).
  const lastSelectionRef = useRef<string>('');

  const rewriteMutation = useMutation({
    mutationFn: async ({ instruction }: { instruction: string }) =>
      api.post<{ rewrittenText: string; rationale?: string; usage: unknown }>(
        `/meetings/${meetingId}/rewrite`,
        {
          selectedText: lastSelectionRef.current,
          instruction,
        },
      ),
    onSuccess: (data) => {
      setRewritten({ text: data.rewrittenText, rationale: data.rationale });
    },
  });

  const hideBubble = useCallback(() => {
    setVisible(false);
    setCustomMode(false);
    setCustomInstruction('');
    setRewritten(null);
    rewriteMutation.reset();
  }, [rewriteMutation]);

  // Track selection
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let raf = 0;
    const handler = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
          // Don't hide if we're inside the bubble itself (mid-interaction)
          const active = document.activeElement;
          if (
            active &&
            bubbleRef.current &&
            bubbleRef.current.contains(active)
          ) {
            return;
          }
          // Don't hide if we're showing a rewrite result or pending
          if (rewritten || rewriteMutation.isPending) return;
          setVisible(false);
          return;
        }

        const range = sel.getRangeAt(0);
        const text = range.toString().trim();
        if (!text) {
          setVisible(false);
          return;
        }

        // Selection must be inside our target container
        if (!container.contains(range.commonAncestorContainer)) {
          setVisible(false);
          return;
        }

        const rect = range.getBoundingClientRect();
        lastSelectionRef.current = text;
        setSelectedText(text);
        setPosition({
          top: rect.top + window.scrollY - 48, // above the selection
          left: rect.left + window.scrollX + rect.width / 2,
        });
        setVisible(true);
      });
    };

    document.addEventListener('selectionchange', handler);
    return () => {
      document.removeEventListener('selectionchange', handler);
      cancelAnimationFrame(raf);
    };
  }, [containerRef, rewritten, rewriteMutation.isPending]);

  if (!visible || !position) return null;

  const errorMessage =
    rewriteMutation.error instanceof ApiError
      ? rewriteMutation.error.message
      : rewriteMutation.error instanceof Error
        ? rewriteMutation.error.message
        : null;

  return (
    <div
      ref={bubbleRef}
      role="toolbar"
      aria-label="Rewrite selection"
      className="fixed z-50 -translate-x-1/2 transform rounded-md border border-smoke/30 bg-white shadow-[0_4px_16px_rgba(0,0,0,0.15)]"
      style={{ top: position.top, left: position.left, minWidth: '280px', maxWidth: '380px' }}
      onMouseDown={(e) => {
        // Don't let clicks collapse the selection
        e.preventDefault();
      }}
    >
      {/* If a rewrite is ready, show Apply/Dismiss */}
      {rewritten ? (
        <div className="p-3 text-xs">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-pine">
            Claude's rewrite
          </div>
          <div className="mb-2 rounded border border-pine/30 bg-pine/5 px-2 py-1.5 text-charcoal">
            {rewritten.text}
          </div>
          {rewritten.rationale && (
            <div className="mb-2 text-[10px] italic text-smoke">{rewritten.rationale}</div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={hideBubble}
              className="text-smoke hover:text-charcoal"
            >
              Dismiss
            </button>
            <button
              type="button"
              onClick={() => {
                onApply({ selectedText, rewrittenText: rewritten.text });
                hideBubble();
              }}
              className="rounded bg-pine px-3 py-1 text-cloud hover:bg-pine/90"
            >
              Apply
            </button>
          </div>
        </div>
      ) : rewriteMutation.isPending ? (
        <div className="flex items-center gap-2 p-3 text-xs text-smoke">
          <span className="animate-pulse">✨</span>
          <span>Asking Claude…</span>
        </div>
      ) : customMode ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (customInstruction.trim()) {
              rewriteMutation.mutate({ instruction: customInstruction.trim() });
            }
          }}
          className="p-2"
        >
          <input
            {...NO_AUTOFILL}
            type="text"
            autoFocus
            value={customInstruction}
            onChange={(e) => setCustomInstruction(e.target.value)}
            placeholder="How should Claude rewrite this?"
            className="w-full rounded border border-smoke/40 bg-white px-2 py-1 text-xs focus:border-pine focus:outline-none"
          />
          <div className="mt-2 flex justify-between text-[11px]">
            <button
              type="button"
              onClick={() => setCustomMode(false)}
              className="text-smoke hover:text-charcoal"
            >
              ← back
            </button>
            <button
              type="submit"
              disabled={!customInstruction.trim()}
              className="rounded bg-pine px-3 py-0.5 text-cloud hover:bg-pine/90 disabled:opacity-50"
            >
              Rewrite
            </button>
          </div>
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-1 p-1.5 text-xs">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => rewriteMutation.mutate({ instruction: p.instruction })}
              className="rounded px-2 py-1 text-charcoal transition-colors hover:bg-pine/10 hover:text-pine"
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setCustomMode(true)}
            className="rounded border border-smoke/30 px-2 py-1 text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Custom…
          </button>
          <button
            type="button"
            onClick={hideBubble}
            className="ml-auto text-smoke hover:text-charcoal"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      )}

      {errorMessage && (
        <div className="rounded-b-md border-t border-red-200 bg-red-50 px-3 py-1.5 text-[11px] text-red-700">
          {errorMessage}
        </div>
      )}
    </div>
  );
}
